import { Box, Stack } from '@mui/material';

import { Card } from '@/components/layout/Card/Card';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { TextEditor } from '@/components/TextEditor/TextEditor';

import type { AdminPromptIterationPage_DocumentSummaryPromptFragment } from '../../graphql/AdminPromptIterationPage.generated';
import { EditorFooter } from './EditorFooter';
import { EditorHeader } from './EditorHeader';

interface PromptEditorPanelProps {
  originalPrompt: AdminPromptIterationPage_DocumentSummaryPromptFragment;
  displayName: string;
  promptMarkdown: string;
  onDisplayNameChange: (name: string) => void;
  onPromptMarkdownChange: (markdown: string) => void;
  readOnly: boolean;
  editorResetKey: number;
  onDuplicate: () => void;
  onOpenImproveModal: () => void;
  onSave: () => void;
  duplicating: boolean;
  improving: boolean;
  saving: boolean;
  hasChanges: boolean;
}

export function PromptEditorPanel({
  originalPrompt,
  displayName,
  promptMarkdown,
  onDisplayNameChange,
  onPromptMarkdownChange,
  readOnly,
  editorResetKey,
  onDuplicate,
  onOpenImproveModal,
  onSave,
  duplicating,
  improving,
  saving,
  hasChanges,
}: PromptEditorPanelProps) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <EditorHeader
        isSystemPrompt={originalPrompt.isSystemPrompt}
        displayName={displayName}
        onDisplayNameChange={onDisplayNameChange}
        onDuplicate={onDuplicate}
        onOpenImproveModal={onOpenImproveModal}
        duplicating={duplicating}
        improving={improving}
        isPromptEmpty={promptMarkdown.trim() === ''}
      />

      <Stack
        sx={{
          flex: 1,
          p: 2,
          minHeight: 0,
          position: 'relative',
        }}
      >
        <TextEditorProvider theme="userGeneratedContent">
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TextEditor
              key={`${originalPrompt.id}-${editorResetKey}`}
              initialValue={promptMarkdown}
              initialValueKind="markdown"
              readOnly={readOnly}
              onUpdate={onPromptMarkdownChange}
            />
          </Box>
        </TextEditorProvider>

        <LoadingOverlay
          open={improving}
          updatingText="Improving prompt (15-30 seconds)..."
        />

        <EditorFooter
          isDirty={hasChanges}
          onSave={onSave}
          saving={saving}
          isSystemPrompt={originalPrompt.isSystemPrompt}
        />
      </Stack>
    </Card>
  );
}
